import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { RequestModel, RequestStatus } from "model";
import { fetchRequestIndex } from "utils/api";

import { LocalStorageFilterValueTypes } from "~components/Organisms/IndexFilters/types";

import { matchesDate, matchesSchool, matchesStatus } from "./utils";

/** Types */
type ActiveRequests = "active-requests";
type PastRequests = "past-requests";
export type RequestType = ActiveRequests | PastRequests;

/** Constants */
export const SegmentName = {
  Unfilled: "unfilled",
  Waitlist: "waitlist",
  Filled: "filled",
  Completed: "completed",
  Canceled: "canceled",
  Review: "review",
} as const;

export type SegmentNameUnion = (typeof SegmentName)[keyof typeof SegmentName];

export const activeSegments: Array<SegmentNameUnion> = [
  SegmentName.Filled,
  SegmentName.Unfilled,
  SegmentName.Waitlist,
];
export const pastSegments: Array<SegmentNameUnion> = [
  SegmentName.Review,
  SegmentName.Completed,
  SegmentName.Unfilled,
  SegmentName.Canceled,
];

export const doesReqMatchSegment = (segment: SegmentNameUnion, req: RequestModel) => {
  switch (segment) {
    case SegmentName.Unfilled:
      return req.status === RequestStatus.Submitted && req.poolCount === 0;
    case SegmentName.Waitlist:
      return (
        (req.status === RequestStatus.Submitted ||
          req.status === RequestStatus.ConfirmationPending) &&
        req.poolCount > 0
      );
    case SegmentName.Filled:
      return req.status === RequestStatus.Filled;
    case SegmentName.Completed:
      return req.status === RequestStatus.Completed;
    case SegmentName.Canceled:
      return req.status === RequestStatus.Canceled;
    case SegmentName.Review:
      return req.status === RequestStatus.Completed && !req.hasFeedback;
    default:
      // eslint-disable-next-line no-console
      console.error("Found a request that doesn't match any segments.");
      return;
  }
};

type UseRequestsParams = {
  showHistory?: boolean;
};
export type UseRequestsReturn = ReturnType<typeof useRequests>["data"];
export function useRequests(params: UseRequestsParams) {
  const { showHistory = false } = params;

  return useQuery({
    queryKey: ["requests", !showHistory ? "active" : "past"],
    queryFn: async () =>
      await fetchRequestIndex({
        showHistory,
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    select: ({ data }) => ({
      requests: data.map((requestData: unknown) => new RequestModel(requestData)),
    }),
  });
}

export function useRequestIndexFilters(
  filters: LocalStorageFilterValueTypes,
  requests?: RequestModel[],
): RequestModel[] {
  return useMemo(() => {
    if (!requests) return [];
    return requests?.filter(
      (item) =>
        matchesStatus(item, filters.statuses) &&
        matchesSchool(item, filters.schools) &&
        matchesDate({
          item,
          dateOption: filters?.dateOption,
          customDate: filters?.customDates,
        }),
    );
  }, [requests, filters]);
}
